import React, {Fragment, Component} from 'react';
import { CircularProgress } from "@mui/material"
import Button from '../components/Button/Button';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import Payment from '../components/Payment/Payment';
import { MainTitle, SecondaryText, ButtonGroup, LoaderWrapper, ReviewWrapper, PayProcessStatusText, Loader } from '../styles/jss/global';
import ReCAPTCHA from "react-google-recaptcha";
import {PageView} from "../model/enums/PageView";
import {ReviewState} from '../model/ReviewState';
import {SubmissionUtils} from "../util/SubmissionUtils";
import {StripeUtils} from "../util/StripeUtils";
import ReviewData from "./ReviewData";
import {SchemaUtils} from "../util/SchemaUtils";
import { normalizeEmptyFields } from '../util/normalizeEmptyFields';

class Review extends Component<{}, ReviewState> {
    componentProps: any;
    componentEvents: any;
    reCaptchaRef: any;
    merchantTransactionId: string | undefined;
    isStripePayment: boolean;
    isPaypalPayment: boolean;
    amountToPay: string;

    constructor(props: any) {
        super(props);
        this.reCaptchaRef = React.createRef();
        this.state = new ReviewState();
        this.isStripePayment = false;
        this.isPaypalPayment = false;
        this.componentProps = {
            showHeader: true,
            showHeaderSocial: true,
            app: {
                name: props.state.app.name,
                id: props.state.app.id,
                logo: props.state.app.logo,
                bgImage: props.state.app.bgImage,
                facebookLink: props.state.app.facebookLink,
                twitterLink: props.state.app.twitterLink,
                linkedinLink: props.state.app.linkedinLink,
                instagramLink: props.state.app.instagramLink,
                tikTokLink: props.state.app.tikTokLink,
                payment: props.state.app.payment,
                config: props.state.app.config,
                version: props.state.app.updatedDate,
                template: props.state.app.template
            },
            appData: props.state.appData,
            rootSchema: props.state.app.schemas,
            stripePayment: {
                paymentMethod: undefined,
                amountToPay: undefined,
                component: undefined
            }
        };
        this.componentEvents = {
            handleShowSpecificPage: props.handleShowSpecificPage,
            handleSuccessSubmitFormData: props.handleSuccessSubmitFormData,
            handleShowErrorPage: props.handleShowErrorPage
        };
        this.amountToPay = this.componentProps.app.payment !== undefined ?
            SchemaUtils.getPaymentPrice(
                this.componentProps.app.payment.config.paymentRules,
                this.componentProps.appData,
                this.componentProps.app.payment.config.price) : "0.00";
    }

    handleSubmitClick = () => {
        this.setState((state: ReviewState) => {
            return {loading: true, validatingRequest: true};
        });
        this.reCaptchaRef.current.execute();
    }

    handlePaymentProcessing = () => {
        this.merchantTransactionId = "";
        this.isStripePayment = true;
        this.handleSubmitClick();
    }

    handleSuccessPurchase = (transactionId?: string) => {
        this.merchantTransactionId = transactionId ?? "";
        this.isPaypalPayment = true;
        this.handleSubmitClick();
    }

    handleShowFormPagesClick = () => {
        this.componentEvents.handleShowSpecificPage(PageView.FormPages);
    }

    handleSubmitAppData = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {submittingAppData: started};
        });
    }

    handlePreparingPayment = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {preparingPayment: started};
        });
    }

    handleCompletingPayment = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {completingPayment: started};
        });
    }

    handlePaymentComplete = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {paymentComplete: started};
        });
    }

    handlePaymentFailed = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {paymentFailed: started};
        });
    }

    handleValidatingRequest = (started: boolean) => {
        this.setState((state: ReviewState) => {
            return {validatingRequest: started};
        });
    }

    handleRecaptchaError = () => {
        this.componentEvents.handleShowErrorPage(
            "The Recaptcha challenge has errored, possibly due to network issues, please refresh the app and start again",
            false,
            null
        );
    }

    handleRecaptchaExpired = () => {
        this.componentEvents.handleShowErrorPage(
            "The Recaptcha challenge has expired, please refresh the app and start again",
            false,
            null
        );
    }

    handleRecaptchaResolved = async (token: any) => {
        if(token === null){
            return;
        }
        this.handleValidatingRequest(false);
        if(this.isStripePayment) {
            this.handleSubmitAppData(true);
            
            let normalizedAppData = normalizeEmptyFields(this.componentProps.appData)

            let submissionResponse = await SubmissionUtils.submitAppData(this.componentProps.app, normalizedAppData,
                token, this.isStripePayment, this.isPaypalPayment, this.merchantTransactionId, this.amountToPay);
            this.handleSubmitAppData(false);
            if (submissionResponse.data.processed) {
                this.handlePreparingPayment(true);
                const stripeIntentData = await StripeUtils.retrieveClientSecret(this.componentProps.app,
                    this.componentProps.stripePayment.amountToPay);
                if (stripeIntentData.clientSecret === undefined) {
                    this.componentEvents.handleShowErrorPage(stripeIntentData.message, true, this.componentProps.appData);
                    return;
                }
                this.handlePreparingPayment(false);
                this.handleCompletingPayment(true);
                let payload =
                    await this.componentProps.stripePayment.component.confirmCardPayment(stripeIntentData.clientSecret,
                        {payment_method: this.componentProps.stripePayment.paymentMethod}
                    );
                const submissionId = submissionResponse.data.submissionId;
                const appVersion = this.componentProps.app.version;
                if (payload.error) {
                    this.handleCompletingPayment(false);
                    this.handlePaymentFailed(true);
                    if(payload.error.payment_intent && payload.error.payment_intent.id){
                        await SubmissionUtils.makePaymentFailedRequest(submissionId, appVersion, payload.error.payment_intent.id);
                    }
                    this.handlePaymentFailed(false);
                    this.componentEvents.handleShowErrorPage(payload.error.message, true, this.componentProps.appData);
                } else {
                    this.handleCompletingPayment(false);
                    this.handlePaymentComplete(true);
                    let response = await SubmissionUtils.makePaymentCompleteRequest(submissionId, appVersion, payload.paymentIntent.id);
                    this.handlePaymentComplete(false);
                    if (response?.data.processed) {
                        this.componentEvents.handleSuccessSubmitFormData(this.componentProps.appData,
                            submissionResponse.data.uniqueIds, response.data.paymentDetails, response.data.processed);
                    } else {
                        this.componentEvents.handleShowErrorPage(response?.data.message, response?.data.app.schemas !== undefined, this.componentProps.appData);
                    }
                }
            } else {
                this.componentEvents.handleShowErrorPage(submissionResponse.data.message,
                    submissionResponse.data.app.schemas !== undefined, this.componentProps.appData,
                    submissionResponse.data.messageItems, submissionResponse.data.actionMessage);
            }
        }else if(this.isPaypalPayment){
            this.handlePaymentComplete(true);

            let normalizedAppData = normalizeEmptyFields(this.componentProps.appData)

            let response = await SubmissionUtils.submitAppData(this.componentProps.app, normalizedAppData,
                token, this.isStripePayment, this.isPaypalPayment, this.merchantTransactionId, this.amountToPay);
            if (response.data.processed) {
                this.handlePaymentComplete(false);
                this.componentEvents.handleSuccessSubmitFormData(this.componentProps.appData, response.data.uniqueIds,
                    response.data.paymentDetails, true);
            } else {
                this.componentEvents.handleShowErrorPage(response.data.message, response.data.app.schemas !== undefined,
                    this.componentProps.appData, response.data.messageItems, response.data.actionMessage);
            }
        }else{
            this.handleSubmitAppData(true);

            let normalizedAppData = normalizeEmptyFields(this.componentProps.appData)

            let response = await SubmissionUtils.submitAppData(this.componentProps.app, normalizedAppData,
                token, this.isStripePayment, this.isPaypalPayment, this.merchantTransactionId, this.amountToPay);
            if (response.data.processed) {
                this.handleSubmitAppData(false);
                this.componentEvents.handleSuccessSubmitFormData(this.componentProps.appData, response.data.uniqueIds,
                    response.data.paymentDetails, false);
            } else {
                this.componentEvents.handleShowErrorPage(response.data.message, response.data.app.schemas !== undefined,
                    this.componentProps.appData, response.data.messageItems, response.data.actionMessage);
            }
        }
    }

    render() {
        const hideSummaryPage = this.componentProps.app.config?.hideSummaryPage;
        const withPaymentNotRequired = this.componentProps.app.payment !== undefined &&
            !this.componentProps.app.payment.config.required && this.amountToPay !== "0.00";
        const showSubmitButton =
            (this.componentProps.app.payment === undefined ||
            this.componentProps.app.payment.config.required === undefined ||
            (!this.componentProps.app.payment.config.required ||
                (this.componentProps.app.payment.config.required && this.amountToPay === "0.00")));
        const submitButtonText = (showSubmitButton && withPaymentNotRequired) ? "Pay Later" : "Submit";

        return (
            <Fragment>
                <Header {...this.componentProps} />

                <LoaderWrapper isLoading={this.state.loading} component="main" className="main" maxWidth="sm">

                    {this.state.loading &&
                        <Loader>
                            {this.state.submittingAppData &&
                                <PayProcessStatusText>
                                    Your request is being submitted, please wait...
                                </PayProcessStatusText>
                            }
                            {this.state.preparingPayment &&
                                <PayProcessStatusText>
                                    Preparing payment, please wait...
                                </PayProcessStatusText>
                            }
                            {this.state.completingPayment &&
                                <PayProcessStatusText>
                                    Completing payment, please wait...
                                </PayProcessStatusText>
                            }
                            {this.state.paymentComplete &&
                                <PayProcessStatusText>
                                    Payment complete. Finalising request...
                                </PayProcessStatusText>
                            }
                            {this.state.paymentFailed &&
                                <PayProcessStatusText>
                                    Payment failed. Finalising request...
                                </PayProcessStatusText>
                            }
                            {this.state.validatingRequest &&
                                <PayProcessStatusText>
                                    Validating request, please wait...
                                </PayProcessStatusText>
                            }
                            <CircularProgress color="primary" size={60} />
                        </Loader>
                    }

                    {this.componentProps.app.id !== "" &&
                    <>
                        <MainTitle component={'h2'} variant={'h3'}>Great, You're Almost Done</MainTitle>
                        {
                            !hideSummaryPage &&
                            <SecondaryText>Please Review Your Data Before Submitting</SecondaryText>
                        }
                        {
                            hideSummaryPage &&
                            <SecondaryText>Please Complete Payment Below</SecondaryText>
                        }
                        {
                            !hideSummaryPage &&
                            <ReviewWrapper>
                                <ReviewData
                                    appData={this.componentProps.appData}
                                    dataSchema={this.componentProps.rootSchema.dataSchema}
                                    uiSchemas={this.componentProps.rootSchema.uiSchema}
                                    showPages={[]}
                                    postSubmission={false}
                                    appTemplate={this.componentProps.app.template}/>
                            </ReviewWrapper>
                        }

                        {this.componentProps.app.payment !== undefined && this.amountToPay !== "0.00" &&
                            <Payment
                                componentProps = {this.componentProps}
                                handleShowErrorPage = {this.componentEvents.handleShowErrorPage}
                                handlePaymentProcessing = {this.handlePaymentProcessing}
                                handleSuccessPurchase = {this.handleSuccessPurchase}
                            />
                        }

                        <ButtonGroup>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={this.handleShowFormPagesClick}
                                classes={{root: 'button-group-item'}}
                            >Back</Button>

                            {showSubmitButton &&
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    size="large"
                                    disableElevation
                                    onClick={this.handleSubmitClick}
                                    classes={{root: 'button-group-item'}}
                                >{submitButtonText}</Button>
                            }
                        </ButtonGroup>
                        <ReCAPTCHA
                            ref={this.reCaptchaRef}
                            size="invisible"
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
                            onChange={this.handleRecaptchaResolved}
                            onErrored={this.handleRecaptchaError}
                            onExpired={this.handleRecaptchaExpired}
                        />
                    </>
                    }
                </LoaderWrapper>

                <Footer />
            </Fragment>
        );
    }
}

export default Review;
